// Liste des interventions : priorité, état, équipe, planification, conflits de synchronisation ;
// liens vers les signalements, les équipes et les conflits ; création d'une intervention programmée.
import { CATEGORIES_SIGNALEMENT, CODES_PRIORITES_INTERVENTION, ETATS_INTERVENTION, LIBELLES_ETAT_INTERVENTION, PRIORITES_INTERVENTION, schemaCreationIntervention } from '@cc/contrats';
import { Bandeau, Bouton, Champ, DateChamp, Dialogue, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Selection, Tableau, useFiltresPersistes, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { AlertTriangle, Megaphone, Plus, Users, Wrench } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerIntervention, INVALIDER_INTERVENTIONS, useEquipes, useInterventions } from './api';
import { TON_ETAT_INTERVENTION, TON_PRIORITE } from './logique';
import type { Intervention } from './types';
import commun from '../../commun/commun.module.css';

export const puceEtat = (i: Pick<Intervention, 'etat'>) => <PuceEtat ton={TON_ETAT_INTERVENTION[i.etat]} libelle={LIBELLES_ETAT_INTERVENTION[i.etat] ?? i.etat} />;
export const pucePriorite = (i: Pick<Intervention, 'priorite' | 'priorite_libelle'>) => <PuceEtat ton={TON_PRIORITE[i.priorite]} libelle={i.priorite_libelle} />;

function FormulaireIntervention({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const naviguer = useNavigate();
  const equipes = useEquipes(ouvert);
  const [v, setV] = useState({ titre: '', categorie: '', description: '', priorite: 'normale', adresse: '', equipe_id: '', planifiee_le: '', cout_estime: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationIntervention.safeParse({
        titre: v.titre,
        categorie: v.categorie,
        description: v.description || undefined,
        priorite: v.priorite,
        adresse: v.adresse || undefined,
        equipe_id: v.equipe_id || undefined,
        planifiee_le: v.planifiee_le || undefined,
        cout_estime: v.cout_estime ? Number(v.cout_estime) : undefined,
      });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerIntervention(corps.data);
    },
    succes: (i) => `Intervention ${i.numero} créée.`,
    invalider: INVALIDER_INTERVENTIONS,
    apres: (i) => void naviguer(`/interventions/${i.id}`),
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Nouvelle intervention programmée"
      description="Intervention décidée par la collectivité sans signalement (entretien, programme de travaux). Une intervention issue d'un signalement se crée depuis sa qualification."
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>
            Créer l&apos;intervention
          </Bouton>
        </>
      }
    >
      <Champ libelle="Titre" value={v.titre} onChange={(e) => setV({ ...v, titre: e.target.value })} erreur={champ('titre')} required />
      <Selection libelle="Catégorie" optionVide="Choisir" options={Object.entries(CATEGORIES_SIGNALEMENT).map(([valeur, libelle]) => ({ valeur, libelle }))} value={v.categorie} onChange={(e) => setV({ ...v, categorie: e.target.value })} erreur={champ('categorie')} required />
      <Selection libelle="Priorité" options={CODES_PRIORITES_INTERVENTION.map((p) => ({ valeur: p, libelle: PRIORITES_INTERVENTION[p] }))} value={v.priorite} onChange={(e) => setV({ ...v, priorite: e.target.value })} />
      <Champ libelle="Adresse ou lieu" value={v.adresse} onChange={(e) => setV({ ...v, adresse: e.target.value })} erreur={champ('adresse') ?? champ('position')} required />
      <ZoneTexte libelle="Description" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} facultatif rows={3} />
      <Selection libelle="Équipe pressentie" optionVide="À désigner plus tard" options={(equipes.data ?? []).filter((e) => e.actif).map((e) => ({ valeur: e.id, libelle: e.nom }))} value={v.equipe_id} onChange={(e) => setV({ ...v, equipe_id: e.target.value })} facultatif />
      <DateChamp libelle="Date prévue" value={v.planifiee_le} onChange={(e) => setV({ ...v, planifiee_le: e.target.value })} facultatif erreur={champ('planifiee_le')} />
      <Champ libelle="Coût estimé (F)" type="number" min={0} value={v.cout_estime} onChange={(e) => setV({ ...v, cout_estime: e.target.value })} facultatif erreur={champ('cout_estime')} />
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageInterventions() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('interventions', { en_cours: 'oui', etat: '', priorite: '' });
  const f = { en_cours: filtres.en_cours ?? '', etat: filtres.etat ?? '', priorite: filtres.priorite ?? '' };
  const liste = useInterventions(f);
  const [creation, setCreation] = useState(false);
  const conflits = (liste.data ?? []).reduce((n, i) => n + i.missions_en_conflit, 0);
  const colonnes: ColonneTableau<Intervention>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (i) => i.numero, cellule: (i) => <Lien href={`/interventions/${i.id}`} className={commun.reference}>{i.numero}</Lien> },
    { id: 'titre', entete: 'Intervention', cellule: (i) => i.titre, valeurTri: (i) => i.titre },
    { id: 'priorite', entete: 'Priorité', cellule: pucePriorite, valeurTri: (i) => ['urgente', 'haute', 'normale', 'basse'].indexOf(i.priorite) },
    { id: 'etat', entete: 'État', cellule: puceEtat, valeurTri: (i) => i.etat },
    { id: 'equipe', entete: 'Équipe', cellule: (i) => i.equipe?.nom ?? 'à désigner' },
    { id: 'planifiee', entete: 'Planifiée le', cellule: (i) => (i.planifiee_le ? date(i.planifiee_le) : 'non planifiée'), valeurTri: (i) => i.planifiee_le },
    { id: 'origine', entete: 'Origine', cellule: (i) => (i.signalement ? <Lien href={`/interventions/signalements/${i.signalement.id}`}>{i.signalement.numero ?? 'signalement'}</Lien> : 'programme') },
    { id: 'missions', entete: 'Missions', numerique: true, cellule: (i) => `${i.missions}${i.missions_en_conflit ? ` (${i.missions_en_conflit} en conflit)` : ''}`, valeurTri: (i) => i.missions },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Interventions (M12)"
        titre="Interventions"
        description="Interventions techniques issues des signalements ou programmées : planification, missions des équipes, rapports de terrain synchronisés, validation et clôture par le responsable technique."
        actions={
          <>
            <LienBouton href="/interventions/signalements" icone={Megaphone}>
              Signalements
            </LienBouton>
            <LienBouton href="/interventions/equipes" icone={Users}>
              Équipes
            </LienBouton>
            {peut(contexte, 'intervention', 'valider') ? (
              <LienBouton href="/interventions/conflits" icone={AlertTriangle}>
                Conflits
              </LienBouton>
            ) : null}
            {peut(contexte, 'intervention', 'creer') ? (
              <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>
                Nouvelle intervention
              </Bouton>
            ) : null}
          </>
        }
      />
      {conflits > 0 ? (
        <Bandeau type="attention">
          {conflits} mission{conflits > 1 ? 's' : ''} en conflit de synchronisation : un rapport reçu du terrain attend la décision du responsable technique (<Lien href="/interventions/conflits">voir les conflits</Lien>).
        </Bandeau>
      ) : null}
      <Filtres
        libelle="Filtres des interventions"
        groupes={[
          { id: 'en_cours', libelle: 'Situation', options: [{ valeur: 'oui', libelle: 'En cours' }, { valeur: 'non', libelle: 'Terminées' }, { valeur: '', libelle: 'Toutes' }] },
          { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...ETATS_INTERVENTION.map((e) => ({ valeur: e, libelle: LIBELLES_ETAT_INTERVENTION[e] }))] },
          { id: 'priorite', libelle: 'Priorité', options: [{ valeur: '', libelle: 'Toutes' }, ...CODES_PRIORITES_INTERVENTION.map((p) => ({ valeur: p, libelle: PRIORITES_INTERVENTION[p] }))] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L3-A" fonction="La liste des interventions">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune intervention" icone={Wrench}>
            <p>Aucune intervention ne correspond à ces filtres. Une intervention naît d&apos;un signalement recevable ou d&apos;un programme.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Interventions" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(i) => i.id} cleColonnes="interventions" />
        )}
      </EtatRequete>
      <FormulaireIntervention ouvert={creation} onFermer={() => setCreation(false)} />
    </>
  );
}
